import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import Icon from './Icon';
import servicesData from '../data/services';

const TIME_SLOTS = ['8:00 AM', '9:00 AM', '10:00 AM', '10:30 AM'];
const VEHICLE_TYPES = ['Sedan', 'SUV', 'Truck', 'VAN'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// ─── timezone-safe date helpers (no UTC parsing) ─────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYMD = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfWeek = (d) => {
  const c = new Date(d);
  c.setDate(c.getDate() - ((c.getDay() + 6) % 7)); // back to Monday
  c.setHours(0, 0, 0, 0);
  return c;
};
const prettyDate = (ymd) =>
  parseYMD(ymd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const prettyFull = (ymd) =>
  parseYMD(ymd).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CAL_HEAD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

// A pending booking whose date is already past — hidden from views (can't approve a past date).
const isStalePending = (b) =>
  (b.status || 'pending') === 'pending' && String(b.date).slice(0, 10) < toYMD(new Date());

const emptyForm = (date = toYMD(new Date())) => ({
  firstName: '', lastName: '', phoneNumber: '', email: '',
  date, time: TIME_SLOTS[0], service: '', vehicleType: 'Sedan', location: 'shop1',
});

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

// Gold "reward ready" tag — shown when the customer has a free reward available,
// or (stronger) when they asked to use it on this visit.
const RewardTag = ({ b }) => ((b && (b.freeReward || b.reward_requested)) ? (
  <span className={`free-badge ${b.reward_requested ? 'using' : ''}`}>
    <Icon name="gift" size={12} /> {b.reward_requested ? 'Using ' : ''}{b.rewardLabel || 'Free Wash'}{b.reward_requested ? '' : ' ready'}
  </span>
) : null);

const StatusBadge = ({ status }) => {
  const s = status || 'pending';
  return <span className={`status-badge status-${s}`}>{s}</span>;
};

// Shared bookings UI. `allowBoard` enables the drag-and-drop schedule board.
// Optionally controlled: pass `view` + `onViewChange` and set `showTabs={false}`
// to drive the view from an external nav (e.g. the manager sidebar).
const BookingsPanel = ({ allowBoard = false, view: controlledView, onViewChange, showTabs = true }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ total: 0, today: 0, pending: 0, confirmed: 0 });
  const [internalView, setInternalView] = useState(allowBoard ? 'board' : 'calendar');
  const view = controlledView || internalView;
  const setView = (v) => { if (onViewChange) onViewChange(v); else setInternalView(v); };
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [calRef, setCalRef] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(toYMD(new Date()));
  const [statView, setStatView] = useState(null); // null | 'today' | 'pending' | 'confirmed'
  const toggleStat = (v) => { setStatView((cur) => (cur === v ? null : v)); if (view !== 'calendar') setView('calendar'); };
  const [filters, setFilters] = useState({ status: '', q: '' });
  const [notifyOnChange, setNotifyOnChange] = useState(true);
  const [modal, setModal] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [headSlot, setHeadSlot] = useState(null);
  const [member, setMember] = useState(null);

  // Render the action bar into the dashboard header if a slot exists there.
  useLayoutEffect(() => { setHeadSlot(document.getElementById('bookings-head-actions')); }, []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const load = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([api.bookings(), api.summary()]);
      setBookings(list);
      setSummary(sum);
      setError('');
    } catch (err) {
      setError(err.message);
      if (/log in/i.test(err.message)) navigate('/portal/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Load the customer's reward points when a booking detail opens.
  useEffect(() => {
    if (modal && modal.mode === 'detail') {
      const b = modal.booking;
      setMember(null);
      api.loyaltyMember(b.phoneNumber).then(setMember).catch(() => {});
    }
  }, [modal]);

  // ─── mutations ─────────────────────────────────────────────────────────────
  const reschedule = async (id, date, time) => {
    const target = bookings.find((b) => b.id === id);
    if (!target || (target.date.slice(0, 10) === date && target.time === time)) return;
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, date, time } : b)));
    try {
      await api.updateBooking(id, { date, time, notify: notifyOnChange });
      flash(`Moved ${target.firstName}'s booking to ${prettyDate(date)} ${time}`);
      load();
    } catch (err) { setError(err.message); load(); }
  };

  const confirmBooking = async (b) => {
    try { await api.confirmBooking(b.id); await load(); flash(`Confirmed ${b.firstName} ${b.lastName}`); }
    catch (err) { setError(err.message); flash(`Confirm failed: ${err.message}`); }
  };

  const purgeStalePending = async () => {
    if (!window.confirm('Delete all pending bookings dated before today? These are old, unconfirmed requests. This cannot be undone (no texts are sent).')) return;
    try { const r = await api.purgeStalePending(); await load(); flash(`Cleared ${r.deleted} old pending booking${r.deleted === 1 ? '' : 's'}`); }
    catch (err) { setError(err.message); flash(`Failed: ${err.message}`); }
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel ${b.firstName} ${b.lastName}'s booking? This deletes it.`)) return;
    try { await api.cancelBooking(b.id, notifyOnChange); flash('Booking cancelled'); load(); }
    catch (err) { setError(err.message); }
  };

  const saveModal = async (e) => {
    e.preventDefault();
    const { form, mode } = modal;
    try {
      if (mode === 'add') { await api.createBooking({ ...form, notify: notifyOnChange }); flash('Booking created'); }
      else { await api.updateBooking(form.id, { ...form, notify: notifyOnChange }); flash('Booking updated'); }
      setModal(null);
      load();
    } catch (err) { setModal((m) => ({ ...m, error: err.message })); }
  };

  // ─── derived ───────────────────────────────────────────────────────────────
  const weekDates = WEEKDAYS.map((_, i) => toYMD(addDays(weekStart, i)));
  const cellBookings = (date, time) =>
    bookings.filter((b) => b.date.slice(0, 10) === date && b.time === time);

  const listBookings = bookings
    // Hide stale past-pending by default, but keep them findable when searching.
    .filter((b) => (filters.q ? true : !isStalePending(b)))
    .filter((b) => (filters.status ? (b.status || 'pending') === filters.status : true))
    .filter((b) => {
      if (!filters.q) return true;
      return `${b.firstName} ${b.lastName} ${b.phoneNumber}`.toLowerCase().includes(filters.q.toLowerCase());
    })
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.time < b.time ? -1 : 1));

  const openEdit = (b) => setModal({ mode: 'edit', form: { ...b, date: b.date.slice(0, 10) } });
  const openDetail = (b) => setModal({ mode: 'detail', booking: b });

  const doComplete = async (b) => {
    try {
      const r = await api.completeBooking(b.id);
      flash(r.message || 'Booking completed');
      setModal(null); load();
    } catch (err) { setError(err.message); }
  };

  const doRedeem = async (b) => {
    try {
      const r = await api.loyaltyRedeem(b.phoneNumber);
      setMember(await api.loyaltyMember(b.phoneNumber));
      flash(r.message || 'Reward redeemed');
    } catch (err) { setError(err.message); }
  };

  const BookingCard = ({ b }) => (
    <div
      className={`booking-card ${draggedId === b.id ? 'dragging' : ''}`}
      draggable={allowBoard}
      onDragStart={(e) => { if (!allowBoard) return; setDraggedId(b.id); e.dataTransfer.effectAllowed = 'move'; }}
      onDragEnd={() => { setDraggedId(null); setDropTarget(null); }}
    >
      <div className="booking-card-top">
        <strong>{b.firstName} {b.lastName}</strong>
        <StatusBadge status={b.status} />
      </div>
      <div className="booking-card-meta">{b.service || '—'} · {b.vehicleType}</div>
      <div className="booking-card-meta"><Icon name="phone" size={13} /> {b.phoneNumber}</div>
      <div className="booking-card-actions">
        {(b.status || 'pending') !== 'confirmed' && <button onClick={() => confirmBooking(b)} title="Confirm"><Icon name="check" size={15} /></button>}
        <button onClick={() => openEdit(b)} title="Edit"><Icon name="edit" size={15} /></button>
        <button onClick={() => cancelBooking(b)} title="Cancel" className="danger"><Icon name="x" size={15} /></button>
      </div>
    </div>
  );

  const actionsBar = (
    <div className="panel-actions">
      <label className="notify-toggle">
        <input type="checkbox" checked={notifyOnChange} onChange={(e) => setNotifyOnChange(e.target.checked)} />
        <span>Text customer on changes</span>
      </label>
      <button className="admin-primary-btn" onClick={() => setModal({ mode: 'add', form: emptyForm() })}><Icon name="plus" size={15} /> Add booking</button>
    </div>
  );

  return (
    <div className="bookings-panel">
      <div className="panel-toolbar">
        {!headSlot && actionsBar}
        <div className="stat-row">
          <div className="stat-card"><i className="stat-ico"><Icon name="calendar" size={20} /></i><span>{summary.total}</span>Total bookings</div>
          <button type="button" className={`stat-card accent clickable ${statView === 'today' ? 'active' : ''}`} onClick={() => toggleStat('today')}><i className="stat-ico"><Icon name="zap" size={20} /></i><span>{summary.today}</span>Today</button>
          <button type="button" className={`stat-card warn clickable ${statView === 'pending' ? 'active' : ''}`} onClick={() => toggleStat('pending')}><i className="stat-ico"><Icon name="clock" size={20} /></i><span>{summary.pending}</span>Pending</button>
          <button type="button" className={`stat-card ok clickable ${statView === 'confirmed' ? 'active' : ''}`} onClick={() => toggleStat('confirmed')}><i className="stat-ico"><Icon name="check-circle" size={20} /></i><span>{summary.confirmed}</span>Confirmed</button>
        </div>
      </div>

      {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}

      {showTabs && (
        <div className="view-tabs">
          <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}><Icon name="calendar" size={15} /> Calendar</button>
          {allowBoard && <button className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}><Icon name="grid" size={15} /> Schedule board</button>}
          <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><Icon name="list" size={15} /> List</button>
        </div>
      )}

      {view === 'calendar' && (() => {
        const daysInMonth = new Date(calRef.getFullYear(), calRef.getMonth() + 1, 0).getDate();
        const lead = (startOfMonth(calRef).getDay() + 6) % 7; // Monday-aligned blanks
        const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
        const todayStr = toYMD(new Date());
        const goMonth = (n) => setCalRef(new Date(calRef.getFullYear(), calRef.getMonth() + n, 1));
        const dayBookings = bookings.filter((b) => b.date.slice(0, 10) === selectedDay)
          .sort((a, b) => (a.time < b.time ? -1 : 1));
        const statList = (statView ? bookings.filter((b) => {
          const st = b.status || 'pending';
          const d = b.date.slice(0, 10);
          if (statView === 'pending') return st === 'pending'; // all pending (incl. stale) so they can be reviewed/cancelled
          if (statView === 'confirmed') return st === 'confirmed';
          if (statView === 'today') return d === todayStr && !isStalePending(b);
          return false;
        }) : []).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
        return (
          <>
            <div className="cal-nav">
              <button onClick={() => goMonth(-1)} aria-label="Previous month"><Icon name="chevron-left" size={16} /></button>
              <strong>{MONTHS[calRef.getMonth()]} {calRef.getFullYear()}</strong>
              <button onClick={() => { setCalRef(startOfMonth(new Date())); setSelectedDay(todayStr); }}>Today</button>
              <button onClick={() => goMonth(1)} aria-label="Next month"><Icon name="chevron-right" size={16} /></button>
            </div>
            <div className="cal-layout">
            <div className="cal-grid">
              {CAL_HEAD.map((d) => <div key={d} className="cal-head">{d}</div>)}
              {cells.map((day, i) => {
                if (!day) return <div key={`b${i}`} className="cal-cell empty" />;
                const ymd = `${calRef.getFullYear()}-${pad(calRef.getMonth() + 1)}-${pad(day)}`;
                const dow = new Date(calRef.getFullYear(), calRef.getMonth(), day).getDay();
                const isWeekend = dow === 0 || dow === 6;
                const dayItems = bookings.filter((b) => b.date.slice(0, 10) === ymd);
                return (
                  <div
                    key={ymd}
                    className={`cal-cell ${isWeekend ? 'weekend' : ''} ${ymd === todayStr ? 'today' : ''} ${ymd === selectedDay ? 'selected' : ''}`}
                    onClick={() => { setSelectedDay(ymd); setStatView(null); }}
                  >
                    <div className="cal-daynum">{day}{dayItems.length > 0 && <span className="cal-count">{dayItems.length}</span>}</div>
                    <div className="cal-chips">
                      {dayItems.slice(0, 3).map((b) => (
                        <button
                          key={b.id}
                          className={`cal-chip ${(b.status || 'pending')}`}
                          onClick={(e) => { e.stopPropagation(); openDetail(b); }}
                          title={`${b.time} · ${b.firstName} ${b.lastName} · ${b.service}`}
                        >
                          <span className="cal-chip-time">{b.time.replace(':00', '')}</span> {b.lastName || b.firstName}{b.freeReward ? <Icon name="gift" size={11} className="cal-chip-gift" /> : null}
                        </button>
                      ))}
                      {dayItems.length > 3 && <span className="cal-more">+{dayItems.length - 3} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="cal-day-detail">
              {statView ? (
                <>
                  <div className="cdd-head">
                    <h3 style={{ textTransform: 'capitalize' }}>{statView} <span className="cdd-count">{statList.length}</span></h3>
                    <div className="cdd-head-actions">
                      {statView === 'pending' && statList.some((b) => b.date.slice(0, 10) < toYMD(new Date())) && (
                        <button className="admin-ghost-btn danger-btn" onClick={purgeStalePending}><Icon name="x" size={14} /> Clear past pending</button>
                      )}
                      <button className="admin-ghost-btn" onClick={() => setStatView(null)}><Icon name="arrow-left" size={14} /> Back to day</button>
                    </div>
                  </div>
                  {statList.length ? (
                    <div className="cdd-list cdd-scroll">
                      {statList.map((b) => (
                        <div key={b.id} className="cdd-item">
                          <span className="cdd-time">{b.time}</span>
                          <div className="cdd-info">
                            <strong>{b.firstName} {b.lastName} <RewardTag b={b} /></strong>
                            <span>{prettyDate(b.date)} · {b.service || '—'} · {b.vehicleType} · <Icon name="phone" size={13} /> {b.phoneNumber}</span>
                          </div>
                          <StatusBadge status={b.status} />
                          <div className="cdd-actions">
                            {(b.status || 'pending') !== 'confirmed' && <button onClick={() => confirmBooking(b)}>Confirm</button>}
                            <button onClick={() => openEdit(b)}>Edit</button>
                            <button className="danger" onClick={() => cancelBooking(b)}>Cancel</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="empty-row">None.</p>}
                </>
              ) : (
                <>
                  <div className="cdd-head">
                    <h3>{prettyFull(selectedDay)}</h3>
                    <button className="admin-primary-btn" onClick={() => setModal({ mode: 'add', form: emptyForm(selectedDay) })}>+ Add booking</button>
                  </div>
                  {dayBookings.length ? (
                    <div className="cdd-list">
                      {dayBookings.map((b) => (
                        <div key={b.id} className="cdd-item">
                          <span className="cdd-time">{b.time}</span>
                          <div className="cdd-info">
                            <strong>{b.firstName} {b.lastName} <RewardTag b={b} /></strong>
                            <span>{b.service || '—'} · {b.vehicleType} · <Icon name="phone" size={13} /> {b.phoneNumber}</span>
                          </div>
                          <StatusBadge status={b.status} />
                          <div className="cdd-actions">
                            {(b.status || 'pending') !== 'confirmed' && <button onClick={() => confirmBooking(b)}>Confirm</button>}
                            <button onClick={() => openEdit(b)}>Edit</button>
                            <button className="danger" onClick={() => cancelBooking(b)}>Cancel</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="empty-row">No bookings on this day.</p>}
                </>
              )}
            </div>
            </div>
          </>
        );
      })()}

      {allowBoard && view === 'board' && (
        <>
          <div className="week-nav">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))}><Icon name="chevron-left" size={15} /> Prev</button>
            <strong>{prettyDate(weekDates[0])} – {prettyDate(weekDates[4])}</strong>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))}>Next <Icon name="chevron-right" size={15} /></button>
          </div>
          <p className="board-hint"><Icon name="move" size={14} /> Drag a booking card onto another day/time to reschedule.</p>
          <div className="board-scroll">
          <div className="board" style={{ gridTemplateColumns: `80px repeat(${WEEKDAYS.length}, 1fr)` }}>
            <div className="board-corner" />
            {weekDates.map((d, i) => (
              <div key={d} className="board-col-head">{WEEKDAYS[i]}<small>{prettyDate(d).replace(/^\w+,?\s*/, '')}</small></div>
            ))}
            {TIME_SLOTS.map((time) => (
              <React.Fragment key={time}>
                <div className="board-time">{time}</div>
                {weekDates.map((date) => {
                  const key = `${date}|${time}`;
                  return (
                    <div
                      key={key}
                      className={`board-cell ${dropTarget === key ? 'drop-target' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDropTarget(key); }}
                      onDragLeave={() => setDropTarget((t) => (t === key ? null : t))}
                      onDrop={(e) => { e.preventDefault(); if (draggedId != null) reschedule(draggedId, date, time); setDropTarget(null); }}
                    >
                      {cellBookings(date, time).map((b) => <BookingCard key={b.id} b={b} />)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          </div>
        </>
      )}

      {view === 'list' && (
        <>
          <div className="list-filters">
            <div className="search-field"><Icon name="search" size={16} /><input placeholder="Search name or phone…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} /></div>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
            </select>
          </div>
          <div className="table-wrap">
            <table className="bookings-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Customer</th><th>Phone</th><th>Service</th><th>Vehicle</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {listBookings.map((b) => (
                  <tr key={b.id}>
                    <td data-label="Date">{prettyDate(b.date)}</td>
                    <td data-label="Time">{b.time}</td>
                    <td className="cell-strong" data-label="Customer">{b.firstName} {b.lastName} <RewardTag b={b} /></td>
                    <td data-label="Phone">{b.phoneNumber}</td>
                    <td data-label="Service">{b.service || '—'}</td>
                    <td data-label="Vehicle">{b.vehicleType}</td>
                    <td data-label="Status"><StatusBadge status={b.status} /></td>
                    <td className="row-actions" data-label="Actions">
                      {(b.status || 'pending') !== 'confirmed' && <button onClick={() => confirmBooking(b)}>Confirm</button>}
                      <button onClick={() => openEdit(b)}>Edit</button>
                      <button className="danger" onClick={() => cancelBooking(b)}>Cancel</button>
                    </td>
                  </tr>
                ))}
                {!listBookings.length && <tr><td colSpan={8} className="empty-row">No bookings match.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modal && modal.mode === 'detail' && createPortal((
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="jg-modal detail-modal" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const b = modal.booking;
              const status = b.status || 'pending';
              return (
                <>
                  <div className="detail-head">
                    <div>
                      <h2>{b.firstName} {b.lastName}</h2>
                      <p className="detail-when">{prettyFull(b.date)} · {b.time}</p>
                    </div>
                    <StatusBadge status={status} />
                  </div>
                  {(b.freeReward || b.reward_requested) && (
                    <div className="detail-reward-banner">
                      <Icon name="gift" size={18} />
                      <span>
                        {b.reward_requested
                          ? <>The customer <strong>asked to use their {b.rewardLabel || 'Free Wash'}</strong> on this visit — redeem it below.</>
                          : <>This customer has a <strong>{b.rewardLabel || 'Free Wash'}</strong> available — redeem it below when they use it.</>}
                      </span>
                    </div>
                  )}
                  <div className="detail-rows">
                    <div className="detail-row"><span>Service</span><strong>{b.service || '—'}</strong></div>
                    <div className="detail-row"><span>Vehicle</span><strong>{b.vehicleType || '—'}</strong></div>
                    <div className="detail-row"><span>Phone</span><strong><a href={`tel:${b.phoneNumber}`}>{b.phoneNumber}</a></strong></div>
                    {b.location && <div className="detail-row"><span>Location</span><strong>{b.location}</strong></div>}
                    <div className="detail-row"><span>Status</span><strong style={{ textTransform: 'capitalize' }}>{status}</strong></div>
                    {b.amount_cents != null && <div className="detail-row"><span>Charged</span><strong>{money(b.amount_cents)}</strong></div>}
                  </div>

                  {(status !== 'completed' || member?.config?.enabled) && (
                    <div className="detail-loyalty">
                      {member?.config?.enabled && (
                        <div className="dl-punch">
                          <div className="dl-points">
                            <span>Punch card</span>
                            <strong>
                              {member.member.punches} / {member.config.punch_goal} details
                              {member.member.free_available > 0 ? ` · ${member.member.free_available} ${member.config.reward_label} ready` : ''}
                            </strong>
                          </div>
                          <div className="punch-dots sm">
                            {Array.from({ length: member.config.punch_goal }).map((_, i) => (
                              <span key={i} className={`punch-dot ${i < member.member.punches ? 'on' : ''}`}>
                                {i < member.member.punches ? <Icon name="check" size={12} /> : null}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {status !== 'completed' && (
                        <button className="admin-primary-btn dl-complete" onClick={() => doComplete(b)}>
                          <Icon name="check-circle" size={15} /> {member?.config?.enabled ? 'Complete detail (add punch)' : 'Mark completed'}
                        </button>
                      )}
                      {member?.config?.enabled && member.member.free_available > 0 && (
                        <button className="admin-ghost-btn dl-redeem" onClick={() => doRedeem(b)}>
                          <Icon name="gift" size={15} /> Redeem a {member.config.reward_label}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="detail-actions">
                    {status !== 'confirmed' && status !== 'completed' && (
                      <button className="admin-primary-btn" onClick={() => { setModal(null); confirmBooking(b); }}><Icon name="check" size={15} /> Confirm</button>
                    )}
                    <button className="admin-ghost-btn" onClick={() => openEdit(b)}><Icon name="edit" size={15} /> Edit</button>
                    <button className="admin-ghost-btn danger-btn" onClick={() => { setModal(null); cancelBooking(b); }}><Icon name="x" size={15} /> Cancel booking</button>
                    <button className="admin-ghost-btn detail-close" onClick={() => setModal(null)}>Close</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ), document.body)}

      {modal && modal.mode !== 'detail' && createPortal((
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <form className="jg-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveModal}>
            <h2>{modal.mode === 'add' ? <><Icon name="plus" size={18} /> Add booking</> : <><Icon name="edit" size={18} /> Edit booking</>}</h2>
            <div className="modal-grid">
              <label>First name<input required value={modal.form.firstName} onChange={(e) => setModal({ ...modal, form: { ...modal.form, firstName: e.target.value } })} /></label>
              <label>Last name<input required value={modal.form.lastName} onChange={(e) => setModal({ ...modal, form: { ...modal.form, lastName: e.target.value } })} /></label>
              <label>Phone<input required value={modal.form.phoneNumber} onChange={(e) => setModal({ ...modal, form: { ...modal.form, phoneNumber: e.target.value } })} /></label>
              <label>Date<input type="date" required value={modal.form.date} onChange={(e) => setModal({ ...modal, form: { ...modal.form, date: e.target.value } })} /></label>
              <label>Time
                <select value={modal.form.time} onChange={(e) => setModal({ ...modal, form: { ...modal.form, time: e.target.value } })}>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>Vehicle
                <select value={modal.form.vehicleType} onChange={(e) => setModal({ ...modal, form: { ...modal.form, vehicleType: e.target.value } })}>
                  {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label className="full">Service
                <select required value={modal.form.service} onChange={(e) => setModal({ ...modal, form: { ...modal.form, service: e.target.value } })}>
                  <option value="">Select service</option>
                  {servicesData.map((s, i) => <option key={i} value={s.title}>{s.title}</option>)}
                </select>
              </label>
            </div>
            {modal.error && <p className="admin-error"><Icon name="alert" size={15} /> {modal.error}</p>}
            <div className="modal-actions">
              <button type="button" className="admin-ghost-btn" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="admin-primary-btn">{modal.mode === 'add' ? 'Create' : 'Save changes'}</button>
            </div>
          </form>
        </div>
      ), document.body)}

      {headSlot && createPortal(actionsBar, headSlot)}
      {toast && createPortal(<div className="toast">{toast}</div>, document.body)}
    </div>
  );
};

export default BookingsPanel;
